import { useToast } from "@chakra-ui/react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import MainContainer from "components/sections/MainContainer";
import HeroHeader from "components/sections/HeroHeader";
import Trans, { useFormatMessage } from "components/base/Trans";
import Text from "components/base/Text";
import Container from "components/base/Container";
import Image from "components/base/Image";
import Link from "components/base/Link";
import {
  MotionFlex as Flex,
  MotionBox as Box,
  appearBelow,
} from "utils/motion";
import FormContact from "components/blocks/FormContact";
import { tagEventForm, getSuccessToastData } from "utils/formHelper";
import MetaHead from "components/base/MetaHead";

export default function Contact() {
  const formatMessage = useFormatMessage();
  const toast = useToast();

  const toastData = getSuccessToastData(
    formatMessage({ id: "LBL_FORM_REGISTER_SUCCESS_TITLE" }),
    formatMessage({ id: "LBL_FORM_REGISTER_SUCCESS_DESCRIPTION" }),
  );

  const contactSuccess = (values, res) => {
    tagEventForm(values.email, values.phone, values.name, res?.pid || null, [
      "contact",
    ]);
    toast(toastData);
  };

  return (
    <MainContainer footerSmall navStyle={{ gradient: true, inverted: false }}>
      <MetaHead
        title="LBL_MENU_CONTACT"
        description="LBL_CONTACT_META_DESC"
        imageUrl="/images/meta/contact.jpg"
      />
      <HeroHeader
        imageSrc={{
          base: "/images/contact/header_mobile.jpg",
          md: "/images/contact/header.jpg",
        }}
        alt="HX50 Heliport - Hill Helicopters"
        title={<Trans id="LBL_CONTACT_TITLE" />}
        subtitle={<Trans id="LBL_CONTACT_SUBTITLE" />}
        align="flex-end"
        height={["50vh", null, "100vh"]}
        imageProps={{ objectPosition: "top" }}
      />
      <Box bg="black.700">
        <Container>
          <Flex
            justify="space-between"
            direction={["column", null, "row"]}
            gap="xxl"
          >
            <Text type="headline_md" color="white" mb="sm">
              <Trans id="LBL_CONTACT_LOCATION" />
            </Text>
            <Box w={["100%", null, "70%"]}>
              <Box h="1px" bg="gray.800" w="100%" />
              <Text type="p_lg" color="white" mt="xxl" mb="md">
                <Trans id="LBL_FOOTER_CONTACTS_TITLE" />
              </Text>
              <Box
                variants={appearBelow}
                display="flex"
                flexDirection="column"
                alignItems="start"
              >
                <Text
                  type="title_sm"
                  mb="2px"
                  textTransform="none"
                  letterSpacing={0}
                >
                  <Trans id="LBL_CONTACT_PHONE_GENERAL_LABEL" />
                </Text>
                <Link
                  href={`tel:${process.env.phone_general}`}
                  mb={2}
                  color="white"
                >
                  <Text type="p_md">{process.env.phone_general}</Text>
                </Link>
                <Text
                  type="title_sm"
                  mb="2px"
                  textTransform="none"
                  letterSpacing={0}
                >
                  <Trans id="LBL_CONTACT_PHONE_ACCOUNTS_LABEL" />
                </Text>
                <Link
                  href={`tel:${process.env.phone_accounts}`}
                  mb={2}
                  color="white"
                >
                  <Text type="p_md">{process.env.phone_accounts}</Text>
                </Link>
                <Text
                  type="title_sm"
                  mb="2px"
                  textTransform="none"
                  letterSpacing={0}
                >
                  <Trans id="LBL_CONTACT_PHONE_SALES_LABEL" />
                </Text>
                <Link
                  href={`tel:${process.env.phone_sales}`}
                  mb={2}
                  color="white"
                >
                  <Text type="p_md">{process.env.phone_sales}</Text>
                </Link>
                <Link
                  fontWeight="bold"
                  href={formatMessage({ id: "LBL_CONTACT_MAIL_LINK" })}
                  color="white"
                >
                  <Trans id="LBL_CONTACT_MAIL_LABEL" />
                </Link>
                <Text type="title_md" color="white" mt="xxl">
                  <Trans id="LBL_FOOTER_ADDRESS_TITLE" />
                </Text>
                <Text type="p_md" color="white" lineHeight={1.5} mt={2} mb={4}>
                  <Trans id="LBL_CONTACT_ADDRESS" />
                </Text>
                <Link
                  fontWeight="bold"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://maps.app.goo.gl/aajGiY3PL84Q21a27"
                  mt="md"
                  mb="xl"
                  color="primary"
                  fontSize="md"
                >
                  <Trans id="LBL_CONTACT_ADDRESS_CTA" />
                </Link>
                <Text type="p_md" color="gray.400">
                  <Trans id="LBL_CONTACT_SCHEDULE_LINE_1" />
                </Text>
                <Text type="p_md" color="gray.400">
                  <Trans id="LBL_CONTACT_SCHEDULE_LINE_2" />
                </Text>
                <Text type="p_md" color="gray.400">
                  <Trans id="LBL_CONTACT_SCHEDULE_LINE_3" />
                </Text>
              </Box>
              <Box h="1px" bg="gray.800" w="100%" my="xxl" />
              <GoogleReCaptchaProvider reCaptchaKey="6Le2QYYUAAAAAHUWJ--OLFPLdeb9JYF2hP8lU4is">
                <FormContact onSuccess={contactSuccess} />
              </GoogleReCaptchaProvider>
            </Box>
          </Flex>
        </Container>
      </Box>
      <Flex bg="black.700">
        <Image
          src={{ base: "/images/contact/facilities_01.jpg" }}
          width={["50%", "50%", "50%", "33%"]}
          height={["206px", null, "400px"]}
          objectFit="cover"
          objectPosition="top"
        />
        <Image
          src={{ base: "/images/contact/facilities_02.jpg" }}
          width={["50%", "50%", "50%", "33%"]}
          height={["206px", null, "400px"]}
          objectFit="cover"
          objectPosition="top"
        />
        <Image
          src={{ base: "/images/contact/facilities_03.jpg" }}
          width={["50%", "50%", "50%", "33%"]}
          display={["none", "none", "none", "block"]}
          height={["206px", null, "400px"]}
          objectFit="cover"
        />
      </Flex>
    </MainContainer>
  );
}
